import { useState, useMemo } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faUser,
  faEnvelope,
  faPhone,
  faStore,
  faSpinner,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { useAuth } from "../../../hooks/useAuth";
import { toastError, toastSuccess } from "../../../utils/toast";

const AddSeller = ({ onAdd, onClose, shops = [], defaultShopId = "" }) => {
  const { register, loading: authLoading } = useAuth();

  const [formData, setFormData] = useState({
    full_name: "",
    phone_number: "",
    email_address: "",
    shopId: defaultShopId || "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const safeShops = useMemo(() => (Array.isArray(shops) ? shops : []), [shops]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone_number") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 9) {
        setFormData((prev) => ({ ...prev, [name]: numericValue }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validatePhoneNumber = (phone) => phone.replace(/\D/g, "").length === 9;

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = "Full name is required";
    }
    if (!formData.phone_number) {
      newErrors.phone_number = "Phone number is required";
    } else if (!validatePhoneNumber(formData.phone_number)) {
      newErrors.phone_number =
        "Please enter exactly 9 digits (e.g., 712345678)";
    }
    if (!formData.email_address) {
      newErrors.email_address = "Email address is required";
    } else if (!validateEmail(formData.email_address)) {
      newErrors.email_address = "Please enter a valid email address";
    }
    if (!formData.shopId) {
      newErrors.shopId = "Please select a shop";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toastError("Please fix all validation errors before continuing.");
      return;
    }

    const userData = {
      full_name: formData.full_name,
      phone: `+255${formData.phone_number}`,
      email: formData.email_address,
      role_type: "seller",
      business_id: formData.shopId,
      password: "Shop@2026",
      password_confirmation: "Shop@2026",
    };

    try {
      setIsSubmitting(true);

      const response = await register(userData);

      if (response?.status === "SUCCESS" || response?.status === 201) {
        toastSuccess(`Seller "${formData.full_name}" added successfully!`);
        if (onAdd) onAdd(response?.data || null);
        onClose();
      } else {
        toastError(
          response?.message || "Failed to add seller. Please try again.",
        );
      }
    } catch (error) {
      if (error?.response?.data?.status === "DUPLICATE_FOUND") {
        const data = error.response.data;
        toastError(
          `User already exists: ${data.data?.full_name} (${data.data?.email})`,
        );
      } else {
        toastError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to add seller. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const loading = isSubmitting || authLoading;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Add new seller"
      maxWidth="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-4 w-4 animate-spin"
                />
                Adding...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Add seller
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faUserPlus}
              className="text-blue-800 h-4 w-4"
            />
          </div>
          <div>
            <p className="text-sm text-gray-600 font-semibold">
              Add new seller
            </p>
            <p className="text-xs text-gray-600 font-medium">
              Fill in all required fields (*)
            </p>
          </div>
        </div>

        {/* Shop */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faStore}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Shop<span className="text-red-700">*</span>
          </Label>
          <Select
            value={formData.shopId}
            onValueChange={(value) => {
              setFormData((prev) => ({ ...prev, shopId: value }));
              if (errors.shopId) setErrors((prev) => ({ ...prev, shopId: "" }));
            }}
            disabled={loading}
          >
            <SelectTrigger
              className={`h-10 text-sm ${
                errors.shopId ? "border-red-500" : "border-gray-300"
              }`}
            >
              <SelectValue placeholder="Select shop" />
            </SelectTrigger>
            <SelectContent>
              {safeShops.map((shop) => (
                <SelectItem key={shop.shopId} value={shop.shopId}>
                  {shop.shop_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.shopId && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.shopId}
            </p>
          )}
        </div>

        {/* Full Name */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faUser}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Full name<span className="text-red-700">*</span>
          </Label>
          <Input
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            placeholder="Enter seller name"
            disabled={loading}
            className={`border ${
              errors.full_name ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.full_name && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.full_name}
            </p>
          )}
        </div>

        {/* Phone */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faPhone}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Phone number<span className="text-red-700">*</span>
          </Label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span className="text-gray-600 text-sm font-medium">+255</span>
            </div>
            <Input
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="712345678"
              maxLength={9}
              disabled={loading}
              className={`border ${
                errors.phone_number ? "border-red-500" : "border-gray-300"
              } rounded-md pl-14 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
            />
          </div>
          <p className="text-xs text-gray-600">
            Enter exactly 9 digits (e.g., 712345678)
          </p>
          {errors.phone_number && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.phone_number}
            </p>
          )}
        </div>

        {/* Email */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faEnvelope}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Email address<span className="text-red-700">*</span>
          </Label>
          <Input
            name="email_address"
            type="email"
            value={formData.email_address}
            onChange={handleChange}
            placeholder="Enter email address"
            disabled={loading}
            className={`border ${
              errors.email_address ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.email_address && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.email_address}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddSeller;
